/**
 * Widget Options & Presets
 * UI select options and static presets used by customization forms.
 */

import type {
  WidgetGradientDirection,
  WidgetButtonShape,
  WidgetBorderStyle,
  WidgetTextCase,
  WidgetIconAnimation,
  WidgetBadgeType,
  WidgetAnimationType,
  WidgetShadowStyle,
  WidgetButtonStyle,
  WidgetPlacement,
} from "../types";

/**
 * Font family options
 */
export const FONT_FAMILY_OPTIONS = [
  { value: '"Readex Pro", system-ui, sans-serif', label: "Readex Pro" },
  { value: "Inter, system-ui, sans-serif", label: "Inter" },
  { value: "system-ui, -apple-system, sans-serif", label: "System UI" },
  { value: "Poppins, sans-serif", label: "Poppins" },
  { value: "Roboto, sans-serif", label: "Roboto" },
  { value: "Open Sans, sans-serif", label: "Open Sans" },
  { value: "Lato, sans-serif", label: "Lato" },
  { value: "Montserrat, sans-serif", label: "Montserrat" },
  { value: "Nunito, sans-serif", label: "Nunito" },
  { value: "Georgia, serif", label: "Georgia" },
  { value: "Arial, sans-serif", label: "Arial" },
];

/**
 * Font weight options
 */
export const FONT_WEIGHT_OPTIONS = [
  { value: 300, label: "Light (300)" },
  { value: 400, label: "Regular (400)" },
  { value: 500, label: "Medium (500)" },
  { value: 600, label: "Semi Bold (600)" },
  { value: 700, label: "Bold (700)" },
  { value: 800, label: "Extra Bold (800)" },
];

/**
 * Shadow style options
 */
export const SHADOW_STYLE_OPTIONS: { value: WidgetShadowStyle; label: string }[] =
  [
    { value: "none", label: "None" },
    { value: "subtle", label: "Subtle" },
    { value: "soft", label: "Soft" },
    { value: "medium", label: "Medium" },
    { value: "strong", label: "Strong" },
  ];

/**
 * Button style options
 */
export const BUTTON_STYLE_OPTIONS: { value: WidgetButtonStyle; label: string }[] =
  [
    { value: "solid", label: "Solid" },
    { value: "outline", label: "Outline" },
    { value: "gradient", label: "Gradient" },
    { value: "glass", label: "Glass" },
  ];

/**
 * Button shape options
 */
export const BUTTON_SHAPE_OPTIONS: { value: WidgetButtonShape; label: string }[] =
  [
    { value: "circle", label: "Circle" },
    { value: "rounded", label: "Rounded" },
    { value: "pill", label: "Pill" },
    { value: "square", label: "Square" },
  ];

/**
 * Border style options
 */
export const BORDER_STYLE_OPTIONS: { value: WidgetBorderStyle; label: string }[] =
  [
    { value: "none", label: "None" },
    { value: "solid", label: "Solid" },
    { value: "dashed", label: "Dashed" },
  ];

/**
 * Text case options
 */
export const TEXT_CASE_OPTIONS: { value: WidgetTextCase; label: string }[] = [
  { value: "none", label: "Normal" },
  { value: "uppercase", label: "UPPERCASE" },
  { value: "lowercase", label: "lowercase" },
  { value: "capitalize", label: "Capitalize" },
];

/**
 * Animation type options
 */
export const ANIMATION_TYPE_OPTIONS: { value: WidgetAnimationType; label: string }[] =
  [
    { value: "none", label: "None" },
    { value: "fade", label: "Fade" },
    { value: "slide", label: "Slide" },
    { value: "scale", label: "Scale" },
    { value: "zoom", label: "Zoom" },
    { value: "bounce", label: "Bounce" },
  ];

/**
 * Icon animation options
 */
export const ICON_ANIMATION_OPTIONS: { value: WidgetIconAnimation; label: string }[] =
  [
    { value: "none", label: "None" },
    { value: "pulse", label: "Pulse" },
    { value: "wave", label: "Wave" },
    { value: "shake", label: "Shake" },
    { value: "bounce", label: "Bounce" },
  ];

/**
 * Badge type options
 */
export const BADGE_TYPE_OPTIONS: { value: WidgetBadgeType; label: string }[] = [
  { value: "none", label: "None" },
  { value: "dot", label: "Dot" },
  { value: "count", label: "Count" },
];

/**
 * Badge position options
 */
export const BADGE_POSITION_OPTIONS = [
  { value: "top-right", label: "Top Right" },
  { value: "top-left", label: "Top Left" },
  { value: "bottom-right", label: "Bottom Right" },
  { value: "bottom-left", label: "Bottom Left" },
];

/**
 * Icon type options
 */
export const ICON_TYPE_OPTIONS = [
  { value: "whatsapp", label: "WhatsApp (Default)" },
  { value: "whatsapp-outline", label: "WhatsApp Outline" },
  { value: "whatsapp-filled", label: "WhatsApp Filled" },
  { value: "message-circle", label: "Message Circle" },
  { value: "message-square", label: "Message Square" },
  { value: "chat-bubble", label: "Chat Bubble" },
  { value: "custom", label: "Custom SVG" },
  { value: "emoji", label: "Emoji" },
];

/**
 * Icon shape options
 */
export const ICON_SHAPE_OPTIONS = [
  { value: "circle", label: "Circle" },
  { value: "square", label: "Square" },
  { value: "rounded", label: "Rounded" },
  { value: "pill", label: "Pill" },
];

/**
 * Icon showcase options (3-5 icons)
 */
export const ICON_SHOWCASE_OPTIONS = [
  { value: "whatsapp", label: "WhatsApp", icon: "whatsapp" },
  { value: "message-circle", label: "Chat", icon: "message-circle" },
  { value: "message-square", label: "Message", icon: "message-square" },
  { value: "chat-bubble", label: "Bubble", icon: "chat-bubble" },
  { value: "heart", label: "Heart", icon: "heart" },
];

/**
 * Close icon options
 */
export const CLOSE_ICON_OPTIONS = [
  { value: "x", label: "X" },
  { value: "close-circle", label: "Close Circle" },
  { value: "arrow-left", label: "Arrow Left" },
  { value: "times", label: "Times" },
];

/**
 * Session window options
 */
export const SESSION_WINDOW_OPTIONS = [
  { value: "ALWAYS", label: "Always Reopen" },
  { value: "ONCE", label: "Once Per Session" },
  { value: "24_HOUR", label: "Every 24 Hours" },
  { value: "7_DAY", label: "Every 7 Days" },
];

/**
 * Gradient direction options
 */
export const GRADIENT_DIRECTION_OPTIONS: {
  value: WidgetGradientDirection;
  label: string;
}[] = [
  { value: "0deg", label: "Top to Bottom" },
  { value: "45deg", label: "Diagonal (TL to BR Soft)" },
  { value: "90deg", label: "Left to Right" },
  { value: "135deg", label: "Diagonal (TL to BR)" },
  { value: "180deg", label: "Bottom to Top" },
  { value: "225deg", label: "Diagonal (BR to TL Soft)" },
  { value: "270deg", label: "Right to Left" },
  { value: "315deg", label: "Diagonal (BR to TL)" },
];

/**
 * Gradient type options
 */
export const GRADIENT_TYPE_OPTIONS = [
  { value: "linear", label: "Linear" },
  { value: "radial", label: "Radial" },
];

/**
 * Position options
 */
export const POSITION_OPTIONS: { value: WidgetPlacement; label: string }[] = [
  { value: "bottom-right", label: "Bottom Right" },
  { value: "bottom-left", label: "Bottom Left" },
  { value: "top-right", label: "Top Right" },
  { value: "top-left", label: "Top Left" },
  { value: "center-modal", label: "Center Modal" },
  { value: "inline-container", label: "Inline Container" },
  { value: "sticky-footer", label: "Sticky Footer (Mobile)" },
];

/**
 * Easing options
 */
export const EASING_OPTIONS = [
  { value: "ease", label: "Ease" },
  { value: "ease-in", label: "Ease In" },
  { value: "ease-out", label: "Ease Out" },
  { value: "ease-in-out", label: "Ease In Out" },
  { value: "linear", label: "Linear" },
];

/**
 * Nudge type options
 */
export const NUDGE_TYPE_OPTIONS = [
  { value: "none", label: "None" },
  { value: "shake", label: "Shake" },
  { value: "bounce", label: "Bounce" },
  { value: "pulse", label: "Pulse" },
];

/**
 * Screen size options for hiding
 */
export const SCREEN_SIZE_OPTIONS = [
  { value: "mobile", label: "Mobile (< 768px)" },
  { value: "tablet", label: "Tablet (768px - 1024px)" },
  { value: "desktop", label: "Desktop (> 1024px)" },
];

// ============================================
// Color Presets
// ============================================

export const COLOR_PRESETS = {
  whatsapp: {
    primary: "#25D366",
    secondary: "#128C7E",
    hover: "#1EBE5A",
  },
  blue: {
    primary: "#2563EB",
    secondary: "#1D4ED8",
    hover: "#1E40AF",
  },
  purple: {
    primary: "#7C3AED",
    secondary: "#6D28D9",
    hover: "#5B21B6",
  },
  pink: {
    primary: "#EC4899",
    secondary: "#DB2777",
    hover: "#BE185D",
  },
  orange: {
    primary: "#F97316",
    secondary: "#EA580C",
    hover: "#C2410C",
  },
  teal: {
    primary: "#14B8A6",
    secondary: "#0D9488",
    hover: "#0F766E",
  },
  red: {
    primary: "#EF4444",
    secondary: "#DC2626",
    hover: "#B91C1C",
  },
  dark: {
    primary: "#1F2937",
    secondary: "#111827",
    hover: "#374151",
  },
};

// ============================================
// Shadow Presets
// ============================================

export const SHADOW_VALUES: Record<WidgetShadowStyle, string> = {
  none: "none",
  subtle: "0 2px 8px rgba(0,0,0,0.06)",
  soft: "0 4px 12px rgba(0,0,0,0.08)",
  medium: "0 4px 16px rgba(0,0,0,0.12)",
  strong: "0 8px 32px rgba(0,0,0,0.18)",
};

// ============================================
// Animation Presets
// ============================================

export const ANIMATION_PRESETS = {
  fade: {
    enter: { from: { opacity: 0 }, to: { opacity: 1 } },
    exit: { from: { opacity: 1 }, to: { opacity: 0 } },
  },
  slide: {
    enter: {
      from: { opacity: 0, transform: "translateY(20px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
    exit: {
      from: { opacity: 1, transform: "translateY(0)" },
      to: { opacity: 0, transform: "translateY(20px)" },
    },
  },
  scale: {
    enter: { from: { opacity: 0, transform: "scale(0.9)" }, to: { opacity: 1, transform: "scale(1)" } },
    exit: { from: { opacity: 1, transform: "scale(1)" }, to: { opacity: 0, transform: "scale(0.9)" } },
  },
  zoom: {
    enter: { from: { opacity: 0, transform: "scale(0.5)" }, to: { opacity: 1, transform: "scale(1)" } },
    exit: { from: { opacity: 1, transform: "scale(1)" }, to: { opacity: 0, transform: "scale(0.5)" } },
  },
  bounce: {
    enter: {
      keyframes:
        "0% { opacity: 0; transform: scale(0.3); } 50% { transform: scale(1.05); } 70% { transform: scale(0.9); } 100% { opacity: 1; transform: scale(1); }",
    },
    exit: { keyframes: "0% { opacity: 1; transform: scale(1); } 100% { opacity: 0; transform: scale(0.3); }" },
  },
};

// ============================================
// Floating Badge Options
// ============================================

export const FLOATING_BADGE_POSITION_OPTIONS = [
  { value: "left", label: "Left" },
  { value: "right", label: "Right" },
  { value: "top", label: "Top" },
  { value: "bottom", label: "Bottom" },
];

export const FLOATING_BADGE_FONT_SIZE_OPTIONS = [
  { value: 12, label: "Small (12px)" },
  { value: 14, label: "Medium (14px)" },
  { value: 16, label: "Large (16px)" },
  { value: 18, label: "Extra Large (18px)" },
];

